import { lazy, StrictMode, Suspense, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import "../../styles/storefront-renderer.css";
import "../../index.scss";
import { i18nReady } from "@/i18n";
import "@/shared/lib/template-assets";
import { installChunkReloadHandler } from "@/shared/lib/chunk-reload";
import {
  installCanvasProbe,
  markEmbeddedOnHtml,
  warnIfHostOriginDiffers,
} from "@/shared/lib/host-lifecycle";
import { isDevicePreviewFrame } from "@/shared/lib/device-preview-frame";
import { axiosInstance } from "@/shared/api/client";
import { TokenManager } from "@/auth";
import { installStudioApi } from "@/api";
import { latchSessionExpired } from "./sessionExpiry";
import { StudioProviders } from "./StudioProviders";
import { HostContextBridge } from "./HostContextBridge";
import { warnIfNoCapabilities } from "./capabilityWarning";
import { studioRoutes } from "./routes";

installChunkReloadHandler();

if (isDevicePreviewFrame()) {
  bootDevicePreviewFrame();
} else {
  bootStudio();
}

/**
 * The gallery modal's device preview — this app framed by ITSELF, same
 * origin, at a device's viewport (see `@/shared/lib/device-preview-frame`).
 *
 * Deliberately none of the Studio's boot: no TokenManager (its host is the
 * Studio, which cannot answer a token request, and the frame fetches nothing),
 * no host bridge, no router. Not marked embedded either — this document is
 * the device's whole screen, so it paints its own surface rather than staying
 * transparent for a host behind it.
 */
function bootDevicePreviewFrame() {
  // What the old about:blank frame reset to: no margin, and no sideways
  // scroll from a block that bleeds past the viewport.
  document.body.style.margin = "0";
  document.body.style.overflowX = "hidden";

  const DevicePreviewFramePage = lazy(
    () => import("@/widgets/template-gallery/DevicePreviewFramePage"),
  );
  renderWhenLanguageReady(
    <StrictMode>
      <Suspense fallback={null}>
        <DevicePreviewFramePage />
      </Suspense>
    </StrictMode>,
  );
}

function bootStudio() {
  // Before React renders anything: tells CSS whether a host sits behind us, so
  // the embedded document can stay transparent and inherit the dashboard's
  // surface instead of guessing at it. In an effect this would land one frame
  // late and flash a mismatched background at the iframe edge.
  markEmbeddedOnHtml();

  // Say so loudly if this build baked in an empty capability allow-list — the
  // gate refuses those calls before the network, so the symptom is a featureless
  // app with a silent console. See capabilityWarning.ts.
  warnIfNoCapabilities();

  // And if it baked in the wrong DASHBOARD in the first place. Same shape of
  // failure, one layer down: every postMessage refused, the token request
  // timing out with the host having heard nothing, and a console full of
  // browser errors naming the symptom rather than the cause. Staging shipped
  // like that (dashboard team, 2026-08-25).
  warnIfHostOriginDiffers();

  // DEV only, and a no-op in a production build: lets the host ask what the
  // canvas actually rendered. The editor is cross-origin from the dashboard and
  // this app's token never leaves memory, so without it there is no way to read
  // the real authenticated canvas at all — see canvasProbe.ts.
  installCanvasProbe();

  // ONE TokenManager for the whole app (2A invariant), shared between the
  // axios layer and the React provider — the 2B swap seam, executed (D8).
  const manager = new TokenManager();
  installStudioApi(axiosInstance, manager, {
    onSessionExpired: () => latchSessionExpired(),
  });

  const router = createBrowserRouter(studioRoutes);

  renderWhenLanguageReady(
    <StrictMode>
      <StudioProviders manager={manager}>
        <HostContextBridge />
        <RouterProvider router={router} />
      </StudioProviders>
    </StrictMode>,
  );
}

/**
 * First render waits for the first language. Translations no longer suspend
 * (`useSuspense: false`, see i18n.ts — a suspending language switch remounted
 * the whole provider tree), so without this the first frames would show raw
 * keys. A failed load still renders: i18next falls back to "en", and a Studio
 * in the wrong language beats a blank frame.
 */
function renderWhenLanguageReady(app: ReactNode) {
  const root = createRoot(document.getElementById("root")!);
  void i18nReady.finally(() => root.render(app));
}
